import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type EventDetail as Ev, type Member, type PersonRef } from '../lib/api';
import {
  ago,
  DISCIPLINES,
  dLong,
  dShort,
  eventEnd,
  hm,
  holdsFullPlace,
  placeKept,
  rel,
  TYPES,
  ymd,
} from '../lib/domain';
import { md } from '../lib/md';
import { PersonPicker, CommentField } from '../fields';
import { CloseButton, Avatar, DiscIcon, ErrorBox, Icon, Loading } from '../ui';
import { useMe, useToast } from '../hooks';
import { dc } from '../lib/style';
import { useBarTitle } from '../barTitle';

function People({
  list,
  ev,
  editable,
  onRemove,
}: {
  list: PersonRef[];
  ev: Ev;
  editable: boolean;
  onRemove: (id: number) => void;
}) {
  return (
    <ul className="people">
      {list.map((p, i) => (
        <li key={`${p.id}-${i}`} style={{ display: 'flex', alignItems: 'center' }}>
          <span className="person">
            <span className="pos tnum">{i + 1}.</span>
            <Avatar person={p} />
            <span className="nm">{p.name}</span>
            {p.id === ev.organizer?.id && <em>Orga</em>}
          </span>
          {editable && p.id && p.id !== ev.organizer?.id && (
            <button
              className="icon-btn"
              style={{ width: 30, height: 30, border: 0 }}
              aria-label={`${p.name} austragen`}
              onClick={() => onRemove(p.id!)}
            >
              <Icon name="x" sw={2} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Why the people signed up were not told: the hourly limit, or an error while sending */
const skipped = (failed?: boolean) =>
  failed ? 'Benachrichtigung fehlgeschlagen' : 'Benachrichtigung übersprungen (zu viele in der letzten Stunde)';

export default function EventDetail() {
  useBarTitle('Termin');
  const id = Number(useParams().id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const { data: e, isLoading, error } = useQuery({ queryKey: ['event', id], queryFn: () => api<Ev>(`/events/${id}`) });
  const [confirm, setConfirm] = useState<'' | 'cancel' | 'delete'>('');
  const [comment, setComment] = useState('');
  const [notifyAll, setNotifyAll] = useState(false);
  const [addId, setAddId] = useState<number | null>(null);
  const members = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/members'), enabled: !!e?.can_edit });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['event', id] });
    void qc.invalidateQueries({ queryKey: ['events'] });
  };
  const onErr = (err: unknown) => toast((err as Error).message);

  const rsvp = useMutation({
    mutationFn: (v: { status: string; kept?: boolean; undo?: boolean }) =>
      api(`/events/${id}/rsvp`, { method: 'PUT', body: { status: v.status } }),
    onSuccess: (_d, v) => {
      refresh();
      if (v.undo) toast('Rückgängig gemacht');
      // a full event: the place is kept a few minutes – say so, with "Rückgängig"
      else if (v.kept) toast(placeKept(v.status), () => rsvp.mutate({ status: 'yes', undo: true }));
    },
    onError: onErr,
  });
  const setPart = useMutation({
    mutationFn: ({ uid, status }: { uid: number; status: string }) =>
      api(`/events/${id}/participants/${uid}`, { method: 'PUT', body: { status } }),
    onSuccess: refresh,
    onError: onErr,
  });
  const cancel = useMutation({
    mutationFn: (cancelled: boolean) =>
      api<{ notified: boolean; people: number; failed?: boolean }>(`/events/${id}/cancel`, {
        method: 'POST',
        body: { cancelled },
      }),
    onSuccess: (r, c) => {
      setConfirm('');
      refresh();
      const what = c ? 'Termin abgesagt' : 'Termin findet wieder statt';
      toast(
        !r.people
          ? what
          : r.notified
            ? `${what} – die Angemeldeten werden benachrichtigt`
            : `${what} – ${skipped(r.failed)}`,
      );
    },
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: () =>
      api<{ notified: boolean; people: number; failed?: boolean }>(`/events/${id}`, { method: 'DELETE' }),
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      toast(
        !r.people
          ? 'Termin gelöscht'
          : r.notified
            ? 'Termin gelöscht – die Angemeldeten bekommen eine Absage'
            : `Termin gelöscht – ${skipped(r.failed)}`,
      );
      void nav('/');
    },
    onError: onErr,
  });
  const addComment = useMutation({
    mutationFn: () => api(`/events/${id}/comments`, { method: 'POST', body: { body: comment, notify_all: notifyAll } }),
    onSuccess: () => {
      setComment('');
      if (notifyAll) toast('Kommentar an alle Angemeldeten geschickt');
      setNotifyAll(false);
      refresh();
    },
    onError: onErr,
  });
  const delComment = useMutation({
    mutationFn: (cid: number) => api(`/comments/${cid}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: onErr,
  });

  if (isLoading) return <Loading />;
  if (error || !e) return <ErrorBox error={error} />;
  const start = new Date(e.starts_at),
    end = eventEnd(e);
  const past = e.past;
  const multi = e.ends_at && ymd(new Date(e.ends_at)) !== ymd(start);
  const when = multi
    ? `${dShort(start)} ${hm(start)} – ${dShort(end)} ${hm(end)} Uhr`
    : `${dLong(start)} · ${e.duration_min || e.ends_at ? `${hm(start)}–${hm(end)}` : hm(start)} Uhr`;
  const inList = new Set([...e.confirmed, ...e.waiting, ...e.maybe].map(p => p.id));

  return (
    <article className="drawer-page" style={dc(e.discipline)}>
      <div className="d-head">
        <CloseButton to="/" label="Zurück zur Liste" />
        <DiscIcon disc={e.discipline} size="lg" />
        <p className="eyebrow">
          {DISCIPLINES[e.discipline]} · {TYPES[e.type]}
          {e.series_id ? ' · Serie' : ''}
        </p>
        <h1 className="d-title">{e.title}</h1>
        <p className="d-when">
          {when}
          {!past && <span className="rel">{rel(start)}</span>}
        </p>
      </div>
      <div className="d-body">
        {e.cancelled && <div className="banner">Dieser Termin wurde abgesagt.</div>}
        {!past && !e.cancelled && (
          <>
            <div
              className={e.capacity ? 'seg seg-rsvp seg-rsvp-2' : 'seg seg-rsvp'}
              role="group"
              aria-label="Deine Teilnahme"
            >
              {(
                [
                  ['yes', 'Dabei'],
                  ['maybe', 'Vielleicht'],
                  ['none', 'Nicht dabei'],
                ] as const
              )
                // limited places: in or out
                .filter(([k]) => k !== 'maybe' || !e.capacity)
                .map(([k, l]) => (
                  <button
                    key={k}
                    aria-pressed={k === e.my_status || (k === 'yes' && e.my_status === 'wait')}
                    disabled={rsvp.isPending}
                    onClick={() =>
                      rsvp.mutate({
                        status: k,
                        kept: k !== 'yes' && holdsFullPlace({ ...e, yes: e.confirmed.length + e.waiting.length }),
                      })
                    }
                  >
                    {l}
                  </button>
                ))}
            </div>
            {e.capacity && e.my_status === 'maybe' && (
              <p className="note warn">
                Für diesen Termin gibt es begrenzte Plätze – bitte entscheide dich: „Dabei“ oder „Nicht dabei“.
                „Vielleicht“ belegt keinen Platz.
              </p>
            )}
            {e.my_status === 'wait' && (
              <p className="note warn">
                Ausgebucht – du stehst auf der Warteliste und rückst automatisch nach, wenn jemand absagt.
              </p>
            )}
          </>
        )}
        {past && <p className="note">Dieser Termin ist vorbei.</p>}
        <dl className="facts">
          {e.location && (
            <div>
              <dt>Ort</dt>
              <dd>
                {e.location} ·{' '}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Karte
                </a>
              </dd>
            </div>
          )}
          {e.meeting_point && (
            <div>
              <dt>Treffpunkt</dt>
              <dd>{e.meeting_point}</dd>
            </div>
          )}
          {e.organizer && (
            <div>
              <dt>Organisation</dt>
              <dd>
                {e.organizer.name}
                {e.organizer.id === me?.id ? ' (du)' : ''}
              </dd>
            </div>
          )}
          {e.capacity && (
            <div>
              <dt>Plätze</dt>
              <dd>
                {e.confirmed.length} / {e.capacity}
                {e.waiting.length
                  ? ` · ${e.waiting.length} auf Warteliste`
                  : e.confirmed.length >= e.capacity
                    ? ' · ausgebucht'
                    : ` · ${e.capacity - e.confirmed.length} frei`}
              </dd>
            </div>
          )}
          {e.updated_at !== e.created_at && (
            <div>
              <dt>Geändert</dt>
              <dd>{ago(new Date(e.updated_at))}</dd>
            </div>
          )}
        </dl>
        {e.can_edit && (
          <div>
            {me && e.organizer_id !== me.id && (
              <p className="note admin-note">
                <Icon name="shield" sw={1.8} />
                Du kannst diesen Termin als Admin bearbeiten – angelegt hat ihn {e.organizer?.name ?? 'jemand anderes'}.
              </p>
            )}
            <div className="d-actions">
              <Link className="btn btn-navy btn-sm" to={`/termine/${e.id}/bearbeiten`}>
                <Icon name="edit" sw={1.8} />
                Bearbeiten
              </Link>
              <Link className="btn btn-ghost btn-sm" to={`/termine/neu?vorlage=${e.id}`}>
                <Icon name="copy" sw={1.8} />
                Duplizieren
              </Link>
              {!past && (
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('cancel')}>
                  <Icon name="ban" sw={1.8} />
                  {e.cancelled ? 'Wieder ansetzen' : 'Absagen'}
                </button>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => setConfirm('delete')}>
                <Icon name="trash" sw={1.8} />
                Löschen
              </button>
            </div>
            {confirm === 'cancel' && (
              <div className="confirm" role="alert">
                <p>
                  {e.cancelled
                    ? 'Termin wieder ansetzen? Die Angemeldeten bekommen eine Nachricht.'
                    : 'Termin absagen? Die Angemeldeten werden sofort benachrichtigt.'}
                </p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('')}>
                  Abbrechen
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => cancel.mutate(!e.cancelled)}>
                  {e.cancelled ? 'Wieder ansetzen' : 'Absagen'}
                </button>
              </div>
            )}
            {confirm === 'delete' && (
              <div className="confirm" role="alert">
                <p>
                  Termin endgültig löschen? Zusagen und Kommentare gehen verloren
                  {!past && !e.cancelled ? ', die Angemeldeten bekommen eine Absage.' : '.'}
                </p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('')}>
                  Abbrechen
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => del.mutate()}>
                  Löschen
                </button>
              </div>
            )}
          </div>
        )}
        {e.description && (
          <section>
            <h2 className="sec-h">Beschreibung</h2>
            <div className="desc" dangerouslySetInnerHTML={{ __html: md(e.description) }} />
          </section>
        )}
        <section>
          <h2 className="sec-h">
            Teilnehmer{' '}
            <span>
              {e.confirmed.length}
              {e.capacity ? ` von ${e.capacity}` : ''}
            </span>
          </h2>
          {e.confirmed.length ? (
            <People
              list={e.confirmed}
              ev={e}
              editable={e.can_edit && !past}
              onRemove={uid => setPart.mutate({ uid, status: 'none' })}
            />
          ) : (
            <p className="note">Noch keine Zusagen.</p>
          )}
          {e.waiting.length > 0 && (
            <>
              <h3 className="sub-h">Warteliste</h3>
              <People
                list={e.waiting}
                ev={e}
                editable={e.can_edit && !past}
                onRemove={uid => setPart.mutate({ uid, status: 'none' })}
              />
            </>
          )}
          {e.maybe.length > 0 && (
            <>
              <h3 className="sub-h">Vielleicht</h3>
              <People
                list={e.maybe}
                ev={e}
                editable={e.can_edit && !past}
                onRemove={uid => setPart.mutate({ uid, status: 'none' })}
              />
            </>
          )}
          {e.can_edit && !past && (
            <div className="cform" style={{ marginTop: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <PersonPicker
                  people={(members.data || []).filter(m => !inList.has(m.id))}
                  value={addId}
                  onChange={setAddId}
                  label="Person eintragen"
                  placeholder="Person eintragen: Name eintippen …"
                  hideLabel
                />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                disabled={!addId}
                onClick={() => {
                  if (addId) setPart.mutate({ uid: addId, status: 'yes' });
                  setAddId(null);
                }}
              >
                Eintragen
              </button>
            </div>
          )}
        </section>
        <section>
          <h2 className="sec-h">
            Kommentare <span>{e.comments.length}</span>
          </h2>
          {e.comments.length > 0 && (
            <ul className="comments">
              {e.comments.map(c => (
                <li key={c.id}>
                  <Avatar person={c.user} />
                  <div>
                    <b>{c.user.name}</b> <small>{ago(new Date(c.created_at))}</small>
                    {c.can_delete && (
                      <>
                        {' '}
                        <button className="linkbtn" style={{ fontSize: 12 }} onClick={() => delComment.mutate(c.id)}>
                          löschen
                        </button>
                      </>
                    )}
                    <p>{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <form
            className="cform"
            onSubmit={ev => {
              ev.preventDefault();
              if (comment.trim() && !addComment.isPending) addComment.mutate(); // never twice
            }}
          >
            <CommentField
              maxLength={2000}
              value={comment}
              onChange={setComment}
              placeholder="Frage, Mitfahrgelegenheit …"
              label="Kommentar schreiben"
            />
            <button className="btn btn-primary btn-sm" disabled={addComment.isPending || !comment.trim()}>
              Senden
            </button>
          </form>
          {e.can_edit && (
            <label
              className="note one-line"
              style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
            >
              <input type="checkbox" checked={notifyAll} onChange={ev => setNotifyAll(ev.target.checked)} />
              Alle Angemeldeten benachrichtigen
            </label>
          )}
          {/* short enough for one line on a small phone (checked by the layout test) */}
          {(e.can_edit || e.organizer?.id) && (
            <p className="note one-line">
              {e.can_edit ? 'Kommentierende bekommen deine Antwort.' : 'Die Orga wird benachrichtigt.'}
            </p>
          )}
        </section>
      </div>
    </article>
  );
}
